import { DropdownArrow } from '@hendrx/icons/DropdownArrow';
import { px } from '@mantine/core';
import { Editor } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FontSizePicker } from 'shared/components/pickers/FontSizePicker';
import { convertCssValueToPixelNumber } from 'shared/utils/convertCssValueToPixelNumber';
import { rem } from 'shared/utils/rem';

import { useTextStyles } from '../../../../utils/tiptap/useTextStyles';
import { WidgetDefaultStyles } from '../../../../utils/typographyStyles';
import { useToolbarSelectStyles } from '../useToolbarSelectStyles';

const getFontSize = (
  getTextStyles: ReturnType<typeof useTextStyles>['getTextStyles'],
  defaultStyles?: WidgetDefaultStyles
) => {
  const fontSize = getTextStyles('fontSize', {
    defaultStyles
  });
  const asNumber = convertCssValueToPixelNumber(fontSize, { property: 'fontSize' });
  return `${px(asNumber)}`;
};

export type TextFontSizeSelectProps = {
  editor: Editor | null;
  defaultStyles?: WidgetDefaultStyles;
};

export function TextFontSizeSelect(props: TextFontSizeSelectProps) {
  const { editor, defaultStyles } = props;
  const { classes, style } = useToolbarSelectStyles({ maxWidth: 46 });
  const { t } = useTranslation('widgets.fontSize');
  const { getTextStyles } = useTextStyles(editor);
  const [activeFontSize, setActiveFontSize] = useState(getFontSize(getTextStyles, defaultStyles));

  useEffect(() => {
    const setFontSize = () => {
      const activeFontSize = getFontSize(getTextStyles, defaultStyles);
      setActiveFontSize(`${activeFontSize}`);
    };

    editor?.on('selectionUpdate', setFontSize);
    editor?.on('update', setFontSize);

    return () => {
      editor?.off('selectionUpdate', setFontSize);
      editor?.off('update', setFontSize);
    };
  }, [editor, getTextStyles]);

  const handleFontSizeChange = (fontSize: number) => {
    editor?.commands.setFontSize(rem(String(fontSize)));
  };

  return (
    <>
      <FontSizePicker
        title={t('title') ?? ''}
        value={Number(activeFontSize)}
        onChange={handleFontSizeChange}
        leftSection={<DropdownArrow />}
        leftSectionWidth={24}
        style={style}
        inputClassNames={{
          root: classes.selectRoot,
          input: classes.selectInput,
          section: classes.selectIcon
        }}
      />
    </>
  );
}
